import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const BackIcon = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>);

const TIME_SIGS = ['2/4','3/4','4/4','5/4','6/8','7/8'];
const SUBDIVISIONS = [
  { id:'quarter', label:'♩', name:'Quarter notes' },
  { id:'eighth', label:'♪♪', name:'Eighth notes' },
  { id:'triplet', label:'♪♪♪', name:'Triplets' },
  { id:'sixteenth', label:'♬', name:'Sixteenth notes' },
];

function useMetronome(bpm, timeSig, subdivision, isPlaying, rhythmTrainer, soundBars, silentBars, rhythmBarCountRef, isSilentRef) {
  const audioCtx = useRef(null);
  const nextBeat = useRef(0);
  const beatCount = useRef(0);
  const scheduler = useRef(null);
  const [currentBeat, setCurrentBeat] = useState(-1);
  const [isSilentNow, setIsSilentNow] = useState(false);

  const beatsPerMeasure = parseInt(timeSig.split('/')[0]);
  const subMult = subdivision === 'eighth' ? 2 : subdivision === 'triplet' ? 3 : subdivision === 'sixteenth' ? 4 : 1;
  const totalPerMeasure = beatsPerMeasure * subMult;

  const getInterval = useCallback(() => {
    let base = 60 / bpm;
    if (subdivision === 'eighth') base /= 2;
    if (subdivision === 'triplet') base /= 3;
    if (subdivision === 'sixteenth') base /= 4;
    return base;
  }, [bpm, subdivision]);

  const scheduleClick = useCallback((time, isAccent, isBeat) => {
    if (!audioCtx.current) return;
    const ctx = audioCtx.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = isAccent ? 1300 : isBeat ? 1000 : 800;
    gain.gain.setValueAtTime(isAccent ? 0.55 : isBeat ? 0.35 : 0.15, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.045);
    osc.start(time); osc.stop(time + 0.05);
  }, []);

  useEffect(() => {
    if (isPlaying) {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtx.current;
      if (ctx.state === 'suspended') ctx.resume();
      nextBeat.current = ctx.currentTime + 0.05;
      beatCount.current = 0;
      if (rhythmTrainer) { rhythmBarCountRef.current = 0; isSilentRef.current = false; }

      const tick = () => {
        while (nextBeat.current < ctx.currentTime + 0.15) {
          const clickIndex = beatCount.current % totalPerMeasure;
          const isBeat = clickIndex % subMult === 0;
          const beatIndex = Math.floor(clickIndex / subMult);
          const isAccent = clickIndex === 0;

          if (rhythmTrainer && clickIndex === 0) {
            const totalCycle = soundBars + silentBars;
            const posInCycle = rhythmBarCountRef.current % totalCycle;
            isSilentRef.current = posInCycle >= soundBars;
            rhythmBarCountRef.current += 1;
            const t = nextBeat.current;
            const silent = isSilentRef.current;
            setTimeout(() => setIsSilentNow(silent), Math.max(0, (t - ctx.currentTime) * 1000 - 20));
          }

          const shouldPlay = !rhythmTrainer || !isSilentRef.current;
          if (shouldPlay) scheduleClick(nextBeat.current, isAccent, isBeat);

          if (isBeat) {
            const t = nextBeat.current;
            const bi = beatIndex;
            const silent = isSilentRef.current;
            setTimeout(() => {
              setCurrentBeat(bi);
              if (!rhythmTrainer) setIsSilentNow(false);
            }, Math.max(0, (t - ctx.currentTime) * 1000 - 20));
          }

          beatCount.current++;
          nextBeat.current += getInterval();
        }
        scheduler.current = setTimeout(tick, 25);
      };
      tick();
    } else {
      clearTimeout(scheduler.current);
      setCurrentBeat(-1);
      setIsSilentNow(false);
    }
    return () => clearTimeout(scheduler.current);
  }, [isPlaying, bpm, timeSig, subdivision, getInterval, scheduleClick, totalPerMeasure, subMult, rhythmTrainer, soundBars, silentBars]);

  return { currentBeat, isSilentNow };
}

export default function MetronomePage() {
  const navigate = useNavigate();
  const [bpm, setBpm] = useState(80);
  const [timeSig, setTimeSig] = useState('4/4');
  const [subdivision, setSubdivision] = useState('quarter');
  const [isPlaying, setIsPlaying] = useState(false);
  const [rhythmTrainer, setRhythmTrainer] = useState(false);
  const [soundBars, setSoundBars] = useState(2);
  const [silentBars, setSilentBars] = useState(2);
  const rhythmBarCount = useRef(0);
  const isSilent = useRef(false);
  const tapTimes = useRef([]);

  const { currentBeat, isSilentNow } = useMetronome(bpm, timeSig, subdivision, isPlaying, rhythmTrainer, soundBars, silentBars, rhythmBarCount, isSilent);

  const beatsPerMeasure = parseInt(timeSig.split('/')[0]);

  const handleTap = () => {
    const now = Date.now();
    tapTimes.current = [...tapTimes.current.filter(t => now - t < 3000), now];
    if (tapTimes.current.length >= 2) {
      const intervals = tapTimes.current.slice(1).map((t, i) => t - tapTimes.current[i]);
      const avg = intervals.reduce((a, b) => a + b) / intervals.length;
      setBpm(Math.round(60000 / avg));
    }
  };

  return (
    <div style={{ fontFamily:"'Barlow', sans-serif", background:'#111009', minHeight:'100vh', color:'#F0EBE3', maxWidth:'var(--app-width, 460px)', margin:'0 auto', paddingBottom:40 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700&family=Barlow+Condensed:wght@400;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        button { cursor: pointer; border: none; font-family: inherit; transition: all 0.15s; }
        button:active { transform: scale(0.96); opacity: 0.82; }
        @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.15)} }
        .beat-dot { transition: all 0.05s; }
        .bg-grid { position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: linear-gradient(rgba(255,85,0,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,85,0,0.02) 1px, transparent 1px);
          background-size: 40px 40px; }
      `}</style>
      <div className="bg-grid"/>

      {/* Header */}
      <div style={{ padding:'52px 20px 20px', background:'linear-gradient(180deg, #FF550015 0%, #111009 100%)', position:'relative', zIndex:1 }}>
        <button onClick={() => { setIsPlaying(false); navigate('/'); }} style={{ background:'transparent', color:'#7A7268', marginBottom:16, display:'flex', alignItems:'center', gap:6, fontSize:13, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1 }}>
          <BackIcon/> HOME
        </button>
        <h1 style={{ fontFamily:"'Barlow Condensed'", fontSize:34, fontWeight:900, letterSpacing:2, color:'#F0EBE3' }}>METRONOME</h1>
      </div>

      <div style={{ padding:'0 20px', position:'relative', zIndex:1 }}>

        {/* Beat dots */}
        <div style={{ display:'flex', justifyContent:'center', gap:12, marginBottom:32 }}>
          {Array.from({ length: beatsPerMeasure }).map((_, i) => {
            const isActive = currentBeat === i;
            const isAccent = i === 0;
            const hidden = rhythmTrainer && isSilentNow;
            return (
              <div key={i} className="beat-dot" style={{
                width: isAccent ? 20 : 14,
                height: isAccent ? 20 : 14,
                borderRadius: '50%',
                background: hidden ? 'transparent' :
                  isActive ? (isAccent ? '#FF5500' : '#F0EBE3') : (isAccent ? '#3A2520' : '#2A2520'),
                border: hidden ? '1.5px solid #2A2520' : 'none',
                boxShadow: isActive && !hidden ? `0 0 ${isAccent?16:10}px ${isAccent?'#FF5500':'#F0EBE3'}88` : 'none',
                transform: isActive && !hidden ? 'scale(1.2)' : 'scale(1)',
                transition: 'all 0.05s',
              }}/>
            );
          })}
        </div>

        {/* BPM display */}
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ fontFamily:"'Barlow Condensed'", fontSize:88, fontWeight:900, color:'#FF5500', lineHeight:1, letterSpacing:-2 }}>{bpm}</div>
          <div style={{ fontSize:12, color:'#5A5248', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:3 }}>BPM</div>
        </div>

        {/* BPM slider */}
        <div style={{ marginBottom:20, padding:'0 4px' }}>
          <input type="range" min={30} max={300} value={bpm} onChange={e => setBpm(parseInt(e.target.value))}
            style={{ width:'100%', accentColor:'#FF5500', height:6 }}/>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#3A3530', marginTop:4 }}>
            <span>30</span><span>300</span>
          </div>
        </div>

        {/* BPM buttons + Tap */}
        <div style={{ display:'flex', gap:8, marginBottom:24, alignItems:'center' }}>
          {[-10,-5,-1].map(d => (
            <button key={d} onClick={() => setBpm(b => Math.max(30, b+d))}
              style={{ flex:1, padding:'10px 0', background:'#1C1916', border:'1px solid #2A2520', borderRadius:10, color:'#C4B9AC', fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:14 }}>{d}</button>
          ))}
          <button onClick={handleTap}
            style={{ flex:2, padding:'10px 0', background:'#2A2318', border:'1.5px solid #FF550055', borderRadius:10, color:'#FF5500', fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:13, letterSpacing:1 }}>
            TAP
          </button>
          {[1,5,10].map(d => (
            <button key={d} onClick={() => setBpm(b => Math.min(300, b+d))}
              style={{ flex:1, padding:'10px 0', background:'#1C1916', border:'1px solid #2A2520', borderRadius:10, color:'#C4B9AC', fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:14 }}>+{d}</button>
          ))}
        </div>

        {/* Time signature */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:2, marginBottom:10 }}>TIME SIG</div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {TIME_SIGS.map(ts => (
              <button key={ts} onClick={() => setTimeSig(ts)}
                style={{ padding:'8px 14px', borderRadius:10, background: timeSig===ts?'#FF5500':'#1C1916', color: timeSig===ts?'#fff':'#7A7268', border:`1px solid ${timeSig===ts?'#FF5500':'#2A2520'}`, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:14 }}>
                {ts}
              </button>
            ))}
          </div>
        </div>

        {/* Subdivision */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:2, marginBottom:10 }}>SUBDIVISION</div>
          <div style={{ display:'flex', gap:6 }}>
            {SUBDIVISIONS.map(s => (
              <button key={s.id} onClick={() => setSubdivision(s.id)}
                style={{ flex:1, padding:'10px 6px', borderRadius:10, background: subdivision===s.id?'#FF5500':'#1C1916', color: subdivision===s.id?'#fff':'#7A7268', border:`1px solid ${subdivision===s.id?'#FF5500':'#2A2520'}`, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:13 }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Rhythm Trainer */}
        <div style={{ background:'#1C1916', border:'1px solid #2A2520', borderRadius:12, padding:'16px', marginBottom:28 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: rhythmTrainer?16:0 }}>
            <div>
              <div style={{ fontFamily:"'Barlow Condensed'", fontSize:13, fontWeight:800, letterSpacing:1.5, color: rhythmTrainer?'#FF5500':'#7A7268' }}>RHYTHM TRAINER</div>
              {!rhythmTrainer && <div style={{ fontSize:11, color:'#4A4038', marginTop:2 }}>Alternates bars of sound and silence</div>}
            </div>
            <button onClick={() => { setRhythmTrainer(r=>!r); rhythmBarCount.current=0; isSilent.current=false; }}
              style={{ width:44, height:24, borderRadius:12, background: rhythmTrainer?'#FF5500':'#2A2520', position:'relative', border:'none', cursor:'pointer', transition:'background 0.2s', flexShrink:0 }}>
              <div style={{ position:'absolute', width:18, height:18, borderRadius:'50%', background:'#fff', top:3, left: rhythmTrainer?23:3, transition:'left 0.2s' }}/>
            </button>
          </div>
          {rhythmTrainer && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[{label:'🔊 Sound bars', val:soundBars, set:setSoundBars, color:'#FF5500'},{label:'🔇 Silent bars', val:silentBars, set:setSilentBars, color:'#7A7268'}].map(({label,val,set,color}) => (
                <div key={label}>
                  <div style={{ fontSize:11, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1, textTransform:'uppercase', marginBottom:8 }}>{label}</div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <button onClick={() => set(v=>Math.max(1,v-1))} style={{ background:'#2A231C', color:'#C4B9AC', width:32, height:32, borderRadius:8, fontSize:18 }}>−</button>
                    <div style={{ flex:1, textAlign:'center', fontFamily:"'Barlow Condensed'", fontSize:28, fontWeight:800, color }}>{val}</div>
                    <button onClick={() => set(v=>Math.min(16,v+1))} style={{ background:'#2A231C', color:'#C4B9AC', width:32, height:32, borderRadius:8, fontSize:18 }}>+</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Play button */}
        <button onClick={() => setIsPlaying(p=>!p)}
          style={{ width:'100%', padding:'20px', background: isPlaying?'#2A2520':'#FF5500', color: isPlaying?'#FF5500':'#fff', borderRadius:16, fontFamily:"'Barlow Condensed'", fontWeight:900, fontSize:22, letterSpacing:3, border: isPlaying?'2px solid #FF5500':'none', display:'flex', alignItems:'center', justifyContent:'center', gap:12 }}>
          {isPlaying ? (
            <><svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg> STOP</>
          ) : (
            <><svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg> START</>
          )}
        </button>
      </div>
    </div>
  );
}
